import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useCustomers } from "@/contexts/CustomerContext";
import { apiRequest } from "@/lib/api";
import { MessageSquare, Plus, Send, ArrowLeft, Search, Sparkles, Loader2, X, Calculator, Trash2, ShieldCheck, Truck, Receipt, DollarSign, Users, UserPlus, Smartphone, Phone } from "lucide-react";
import "./Messages.css";

interface EstimateLineItem {
  id: string;
  description: string;
  qty: number;
  price: number;
}

interface EstimateState {
  items: EstimateLineItem[];
  taxRate: number;
  warranty: boolean;
  warrantyPrice: number;
  delivery: boolean;
  deliveryFee: number;
  customerName: string;
}

interface LastMessage {
  content: string;
  sender: string;
  timestamp: string;
}

interface Conversation {
  id: string;
  customerId: string;
  customerName: string;
  customerPhone: string;
  salesUserId: string;
  salesUserName: string;
  createdAt: string;
  lastMessageAt: string;
  lastMessage?: LastMessage;
}

interface ChatMessage {
  id: string;
  conversationId: string;
  sender: "staff" | "customer";
  content: string;
  timestamp: string;
}

interface ConversationDetail {
  conversation: Conversation;
  messages: ChatMessage[];
}

interface SuggestedReply {
  text: string;
  tone: string;
}

export default function Messages() {
  const { user } = useAuth();
  const { customers } = useCustomers();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConvId, setActiveConvId] = useState<string | null>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [activeConv, setActiveConv] = useState<Conversation | null>(null);
  const [search, setSearch] = useState("");
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);
  const [newConvMode, setNewConvMode] = useState<"existing" | "manual">("existing");
  const [customerSearch, setCustomerSearch] = useState("");
  const [newName, setNewName] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [creatingConv, setCreatingConv] = useState(false);
  const [tab, setTab] = useState<"my" | "all">("my");
  const [channel, setChannel] = useState<"chat" | "sms">("chat");
  const [suggestions, setSuggestions] = useState<SuggestedReply[]>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showEstimate, setShowEstimate] = useState(false);
  const [estimate, setEstimate] = useState<EstimateState>({
    items: [{ id: "1", description: "", qty: 1, price: 0 }],
    taxRate: 0,
    warranty: false,
    warrantyPrice: 299,
    delivery: false,
    deliveryFee: 149,
    customerName: "",
  });
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const smsEndRef = useRef<HTMLDivElement>(null);

  const [smsThreads, setSmsThreads] = useState<{phone: string; name: string; lastMessage: string; lastTime: string; count: number}[]>([]);
  const [activeSmsPhone, setActiveSmsPhone] = useState<string | null>(null);
  const [smsMessages, setSmsMessages] = useState<any[]>([]);
  const [smsInput, setSmsInput] = useState("");
  const [smsSending, setSmsSending] = useState(false);
  const [smsSearch, setSmsSearch] = useState("");
  const [showNewSms, setShowNewSms] = useState(false);
  const [newSmsPhone, setNewSmsPhone] = useState("");
  const [newSmsName, setNewSmsName] = useState("");
  const [smsEnabled, setSmsEnabled] = useState(false);

  const fetchSmsConfig = useCallback(async () => {
    if (!user?.storeId) return;
    try {
      const data = await apiRequest<any>(`/sms/config?storeId=${user.storeId}`);
      setSmsEnabled(data.smsEnabled || false);
    } catch {}
  }, [user?.storeId]);

  const fetchSmsThreads = useCallback(async () => {
    if (!user?.storeId) return;
    try {
      const data = await apiRequest<any>(`/sms/threads?storeId=${user.storeId}`);
      setSmsThreads(data.threads || []);
    } catch {}
  }, [user?.storeId]);

  const fetchSmsConvo = useCallback(async (phone: string) => {
    if (!user?.storeId) return;
    try {
      const data = await apiRequest<any>(`/sms/messages?storeId=${user.storeId}&phone=${encodeURIComponent(phone)}`);
      setSmsMessages(data.messages || []);
    } catch {}
  }, [user?.storeId]);

  useEffect(() => {
    if (channel === "sms") {
      fetchSmsConfig();
      fetchSmsThreads();
    }
  }, [channel, fetchSmsConfig, fetchSmsThreads]);

  useEffect(() => {
    if (channel === "sms" && activeSmsPhone) {
      fetchSmsConvo(activeSmsPhone);
      const interval = setInterval(() => fetchSmsConvo(activeSmsPhone), 5000);
      return () => clearInterval(interval);
    }
  }, [channel, activeSmsPhone, fetchSmsConvo]);

  useEffect(() => {
    if (channel === "sms" && smsEndRef.current) {
      smsEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [smsMessages, channel]);

  const sendSms = async () => {
    if (!smsInput.trim() || !activeSmsPhone || !user?.storeId) return;
    setSmsSending(true);
    try {
      const thread = smsThreads.find(t => t.phone === activeSmsPhone);
      await apiRequest("/sms/send", {
        method: "POST",
        body: JSON.stringify({
          storeId: user.storeId,
          to: activeSmsPhone,
          message: smsInput.trim(),
          customerName: thread?.name || "",
          userId: user.id,
          userName: user.name,
        }),
      });
      setSmsInput("");
      fetchSmsConvo(activeSmsPhone);
      fetchSmsThreads();
    } catch (err: any) {
      alert(err.message || "Failed to send SMS");
    }
    setSmsSending(false);
  };

  const startNewSms = async () => {
    if (!newSmsPhone.trim()) return;
    const phone = newSmsPhone.trim().startsWith("+") ? newSmsPhone.trim() : `+1${newSmsPhone.trim().replace(/\D/g, "")}`;
    setActiveSmsPhone(phone);
    setShowNewSms(false);
    setNewSmsPhone("");
    setNewSmsName("");
    fetchSmsConvo(phone);
    fetchSmsThreads();
  };

  const isManagerOrAdmin = user?.role === "admin" || user?.role === "manager";

  const fetchConversations = useCallback(async () => {
    try {
      let res: { conversations: Conversation[] };
      const storeParam = user?.storeId ? `&storeId=${user.storeId}` : "";
      if (isManagerOrAdmin && tab === "all") {
        res = await apiRequest<{ conversations: Conversation[] }>(`/messages/conversations/all?storeId=${user?.storeId || ""}`);
      } else {
        res = await apiRequest<{ conversations: Conversation[] }>(`/messages/conversations?userId=${user?.id}${storeParam}`);
      }
      setConversations(res.conversations || []);
    } catch {}
  }, [user?.id, isManagerOrAdmin, tab]);

  useEffect(() => {
    fetchConversations();
    const interval = setInterval(fetchConversations, 5000);
    return () => clearInterval(interval);
  }, [fetchConversations]);

  const openConversation = useCallback(async (convId: string) => {
    setActiveConvId(convId);
    setSuggestions([]);
    setShowSuggestions(false);
    setShowEstimate(false);
    try {
      const data = await apiRequest<ConversationDetail>(`/messages/conversations/${convId}`);
      setActiveConv(data.conversation);
      setChatMessages(data.messages);
    } catch {}
  }, []);

  const lastMsgTimestampRef = useRef("");

  useEffect(() => {
    const msgs = chatMessages;
    if (msgs.length > 0) {
      lastMsgTimestampRef.current = msgs[msgs.length - 1].timestamp;
    }
  }, [chatMessages]);

  useEffect(() => {
    if (!activeConvId) return;
    const poll = setInterval(async () => {
      try {
        const since = lastMsgTimestampRef.current || "";
        const res = await apiRequest<{ messages: ChatMessage[] }>(
          `/messages/conversations/${activeConvId}/messages?since=${encodeURIComponent(since)}`
        );
        const newMsgs = res.messages || [];
        if (newMsgs.length > 0) {
          setChatMessages((prev) => {
            const existingIds = new Set(prev.map((m) => m.id));
            const fresh = newMsgs.filter((m) => !existingIds.has(m.id));
            return fresh.length > 0 ? [...prev, ...fresh] : prev;
          });
        }
      } catch {}
    }, 3000);
    pollRef.current = poll;
    return () => clearInterval(poll);
  }, [activeConvId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  const sendMessage = async (text?: string) => {
    const msgText = text || input.trim();
    if (!msgText || !activeConvId || sending) return;
    setSending(true);
    setShowSuggestions(false);
    setSuggestions([]);
    try {
      const msg = await apiRequest<ChatMessage>(`/messages/conversations/${activeConvId}/messages`, {
        method: "POST",
        body: JSON.stringify({ sender: "staff", content: msgText }),
      });
      setChatMessages((prev) => [...prev, msg]);
      setInput("");
      fetchConversations();
    } catch {}
    setSending(false);
  };

  const generateSuggestions = async () => {
    if (!activeConv || chatMessages.length === 0) return;
    setIsLoadingSuggestions(true);
    setShowSuggestions(true);
    setSuggestions([]);
    try {
      const data = await apiRequest<{ suggestions: SuggestedReply[] }>("/messages/suggest-reply", {
        method: "POST",
        body: JSON.stringify({
          customerName: activeConv.customerName,
          customerPhone: activeConv.customerPhone,
          conversationHistory: chatMessages.map((m) => ({ sender: m.sender, content: m.content })),
        }),
      });
      setSuggestions(data.suggestions || []);
    } catch {
      setSuggestions([]);
    }
    setIsLoadingSuggestions(false);
  };

  const useSuggestion = (text: string) => {
    setInput(text);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const sendSuggestionDirectly = (text: string) => {
    sendMessage(text);
  };

  const openEstimate = () => {
    setShowEstimate(true);
    setEstimate({
      items: [{ id: Date.now().toString(), description: "", qty: 1, price: 0 }],
      taxRate: 0,
      warranty: false,
      warrantyPrice: 299,
      delivery: false,
      deliveryFee: 149,
      customerName: activeConv?.customerName || "",
    });
  };

  const addLineItem = () => {
    setEstimate((prev) => ({
      ...prev,
      items: [...prev.items, { id: Date.now().toString(), description: "", qty: 1, price: 0 }],
    }));
  };

  const removeLineItem = (id: string) => {
    setEstimate((prev) => ({
      ...prev,
      items: prev.items.length > 1 ? prev.items.filter((i) => i.id !== id) : prev.items,
    }));
  };

  const updateLineItem = (id: string, field: keyof EstimateLineItem, value: string | number) => {
    setEstimate((prev) => ({
      ...prev,
      items: prev.items.map((i) => (i.id === id ? { ...i, [field]: value } : i)),
    }));
  };

  const estimateSubtotal = estimate.items.reduce((sum, i) => sum + i.qty * i.price, 0);
  const estimateWarrantyTotal = estimate.warranty ? estimate.warrantyPrice : 0;
  const estimateDeliveryTotal = estimate.delivery ? estimate.deliveryFee : 0;
  const estimatePreTax = estimateSubtotal + estimateWarrantyTotal + estimateDeliveryTotal;
  const estimateTax = estimatePreTax * (estimate.taxRate / 100);
  const estimateTotal = estimatePreTax + estimateTax;

  const formatCurrency = (n: number) => "$" + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  const sendEstimate = () => {
    const validItems = estimate.items.filter((i) => i.description.trim() && i.price > 0);
    if (validItems.length === 0) return;
    let msg = `📋 *Easy Estimate* for ${estimate.customerName || "Customer"}\n`;
    msg += "━━━━━━━━━━━━━━━━━━━━\n";
    validItems.forEach((item, idx) => {
      msg += `${idx + 1}. ${item.description}  ×${item.qty}  ${formatCurrency(item.qty * item.price)}\n`;
    });
    msg += "━━━━━━━━━━━━━━━━━━━━\n";
    msg += `Subtotal: ${formatCurrency(estimateSubtotal)}\n`;
    if (estimate.warranty) {
      msg += `🛡️ 5-Year Warranty: ${formatCurrency(estimateWarrantyTotal)}\n`;
    }
    if (estimate.delivery) {
      msg += `🚚 Delivery: ${formatCurrency(estimateDeliveryTotal)}\n`;
    }
    if (estimate.taxRate > 0) {
      msg += `Tax (${estimate.taxRate}%): ${formatCurrency(estimateTax)}\n`;
    }
    msg += "━━━━━━━━━━━━━━━━━━━━\n";
    msg += `💰 TOTAL: ${formatCurrency(estimateTotal)}`;
    sendMessage(msg);
    setShowEstimate(false);
  };

  const selectExistingCustomer = (custId: string) => {
    const cust = customers.find((c) => c.id === custId);
    if (!cust) return;
    setNewName(cust.name);
    setNewPhone(cust.phone);
    setNewConvMode("manual");
  };

  const filteredCustomers = customers.filter((c) => {
    if (!customerSearch.trim()) return true;
    const q = customerSearch.toLowerCase();
    return c.name.toLowerCase().includes(q) || c.phone.includes(q) || c.email.toLowerCase().includes(q);
  });

  const createConversation = async () => {
    if (!newName.trim() || !newPhone.trim() || creatingConv) return;
    setCreatingConv(true);
    try {
      const conv = await apiRequest<Conversation>("/messages/conversations", {
        method: "POST",
        body: JSON.stringify({
          customerId: "cust-" + Date.now(),
          customerName: newName.trim(),
          customerPhone: newPhone.trim(),
          salesUserId: user?.id,
          salesUserName: user?.name,
          storeId: user?.storeId,
        }),
      });
      setShowNewModal(false);
      setNewName("");
      setNewPhone("");
      setCustomerSearch("");
      setNewConvMode("existing");
      await fetchConversations();
      openConversation(conv.id);
    } catch {}
    setCreatingConv(false);
  };

  const filteredConversations = conversations.filter((c) =>
    c.customerName.toLowerCase().includes(search.toLowerCase()) ||
    c.customerPhone.includes(search)
  );

  const formatTime = (ts: string) => {
    const d = new Date(ts);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    if (diff < 86400000) {
      return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    }
    if (diff < 86400000 * 7) {
      return d.toLocaleDateString([], { weekday: "short" });
    }
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
  };

  const getInitials = (name: string) =>
    name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);

  const toneEmoji = (tone: string) => {
    if (tone === "friendly") return "😊";
    if (tone === "professional") return "💼";
    if (tone === "sales") return "🎯";
    return "💬";
  };

  const chatOpen = activeConvId !== null;

  const filteredSmsThreads = smsThreads.filter(t =>
    t.name.toLowerCase().includes(smsSearch.toLowerCase()) ||
    t.phone.includes(smsSearch)
  );
  const smsOpen = activeSmsPhone !== null;

  return (
    <div className={`messages-page ${channel === "chat" && chatOpen ? "chat-open" : ""} ${channel === "sms" && smsOpen ? "chat-open" : ""}`}>
      <div className="messages-sidebar">
        <div className="messages-sidebar-header">
          <h2>Messages</h2>
          <div className="channel-tabs">
            <button className={`channel-tab ${channel === "chat" ? "active" : ""}`} onClick={() => setChannel("chat")}>
              <MessageSquare size={14} /> Chat
            </button>
            <button className={`channel-tab ${channel === "sms" ? "active" : ""}`} onClick={() => setChannel("sms")}>
              <Smartphone size={14} /> Text Messages
            </button>
          </div>
          {channel === "chat" && isManagerOrAdmin && (
            <div className="messages-tabs">
              <button className={`messages-tab ${tab === "my" ? "active" : ""}`} onClick={() => setTab("my")}>
                My Conversations
              </button>
              <button className={`messages-tab ${tab === "all" ? "active" : ""}`} onClick={() => setTab("all")}>
                All Conversations
              </button>
            </div>
          )}
          {channel === "chat" && (
          <div className="messages-search-row">
            <input
              className="messages-search"
              type="text"
              placeholder="Search conversations..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button className="messages-new-btn" onClick={() => setShowNewModal(true)} title="New Conversation">
              <Plus size={18} />
            </button>
          </div>
          )}
          {channel === "sms" && (
          <div className="messages-search-row">
            <input
              className="messages-search"
              type="text"
              placeholder="Search text threads..."
              value={smsSearch}
              onChange={(e) => setSmsSearch(e.target.value)}
            />
            <button className="messages-new-btn" onClick={() => setShowNewSms(true)} title="New Text">
              <Plus size={18} />
            </button>
          </div>
          )}
        </div>
        {channel === "chat" && filteredConversations.length === 0 ? (
          <div className="messages-empty-sidebar">
            <MessageSquare size={40} />
            <p>No conversations yet</p>
          </div>
        ) : channel === "chat" ? (
          <div className="conversations-list">
            {filteredConversations.map((c) => (
              <div
                key={c.id}
                className={`conversation-item ${activeConvId === c.id ? "active" : ""}`}
                onClick={() => openConversation(c.id)}
              >
                <div className="conv-avatar">{getInitials(c.customerName)}</div>
                <div className="conv-info">
                  <div className="conv-top-row">
                    <span className="conv-name">{c.customerName}</span>
                    <span className="conv-time">{formatTime(c.lastMessageAt)}</span>
                  </div>
                  <div className="conv-preview">
                    {c.lastMessage
                      ? `${c.lastMessage.sender === "staff" ? "You: " : ""}${c.lastMessage.content}`
                      : c.customerPhone}
                  </div>
                  {isManagerOrAdmin && tab === "all" && (
                    <span className="conv-sales-badge">{c.salesUserName}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {channel === "sms" && !smsEnabled ? (
          <div className="messages-empty-sidebar">
            <Smartphone size={40} />
            <p>SMS not configured</p>
            <p style={{ fontSize: 12, color: "#94A3B8", marginTop: 4 }}>Set up Twilio in Settings to enable text messaging</p>
          </div>
        ) : channel === "sms" && filteredSmsThreads.length === 0 && !showNewSms ? (
          <div className="messages-empty-sidebar">
            <Smartphone size={40} />
            <p>No text threads yet</p>
            <p style={{ fontSize: 12, color: "#94A3B8", marginTop: 4 }}>Start a new text conversation</p>
          </div>
        ) : channel === "sms" && smsEnabled ? (
          <div className="conversations-list">
            {filteredSmsThreads.map((t) => (
              <div
                key={t.phone}
                className={`conversation-item ${activeSmsPhone === t.phone ? "active" : ""}`}
                onClick={() => { setActiveSmsPhone(t.phone); fetchSmsConvo(t.phone); }}
              >
                <div className="conv-avatar" style={{ background: "#8B5CF620", color: "#8B5CF6" }}><Phone size={16} /></div>
                <div className="conv-info">
                  <div className="conv-top-row">
                    <span className="conv-name">{t.name}</span>
                    <span className="conv-time">{formatTime(t.lastTime)}</span>
                  </div>
                  <div className="conv-preview">{t.lastMessage}</div>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {channel === "sms" ? (
        <div className="messages-chat-panel">
          {activeSmsPhone ? (
            <>
              <div className="messages-chat-header">
                <button className="messages-back-btn" onClick={() => setActiveSmsPhone(null)}>
                  <ArrowLeft size={20} />
                </button>
                <div className="conv-avatar" style={{ background: "#8B5CF620", color: "#8B5CF6" }}><Phone size={16} /></div>
                <div className="messages-chat-header-info">
                  <h3>{smsThreads.find(t => t.phone === activeSmsPhone)?.name || activeSmsPhone}</h3>
                  <span>{activeSmsPhone}</span>
                </div>
              </div>
              <div className="messages-chat-body">
                {smsMessages.map((m) => (
                  <div key={m.id} className={`chat-bubble ${m.direction === "outbound" ? "staff" : "customer"}`}>
                    <div className="bubble-content">{m.content}</div>
                    <div className="bubble-meta">
                      {m.direction === "outbound" && m.sentByName && <span>{m.sentByName} &middot; </span>}
                      {formatTime(m.createdAt)}
                    </div>
                  </div>
                ))}
                <div ref={smsEndRef} />
              </div>
              <div className="messages-chat-input">
                <input
                  placeholder="Type a text message..."
                  value={smsInput}
                  onChange={(e) => setSmsInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendSms()}
                  disabled={smsSending}
                />
                <button onClick={sendSms} disabled={!smsInput.trim() || smsSending} className="send-btn">
                  {smsSending ? <Loader2 size={18} className="spin" /> : <Send size={18} />}
                </button>
              </div>
            </>
          ) : (
            <div className="messages-empty-chat">
              <Smartphone size={48} />
              <h3>Text Messages</h3>
              <p>Select a thread or start a new text conversation</p>
            </div>
          )}

          {showNewSms && (
            <div className="new-conv-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowNewSms(false); }}>
              <div className="new-conv-modal">
                <h3>New Text Message</h3>
                <div className="form-group">
                  <label>Phone Number</label>
                  <input value={newSmsPhone} onChange={(e) => setNewSmsPhone(e.target.value)} placeholder="+1 (555) 123-4567" />
                </div>
                <div className="form-group">
                  <label>Name (optional)</label>
                  <input value={newSmsName} onChange={(e) => setNewSmsName(e.target.value)} placeholder="Customer name" />
                </div>
                <div className="new-conv-actions">
                  <button className="new-conv-cancel" onClick={() => setShowNewSms(false)}>Cancel</button>
                  <button className="new-conv-submit" onClick={startNewSms} disabled={!newSmsPhone.trim()}>
                    Open Thread
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
      <div className="messages-chat-panel">
        {activeConv ? (
          <>
            <div className="messages-chat-header">
              <button className="messages-back-btn" onClick={() => { setActiveConvId(null); setActiveConv(null); setShowEstimate(false); }}>
                <ArrowLeft size={20} />
              </button>
              <div className="conv-avatar">{getInitials(activeConv.customerName)}</div>
              <div className="messages-chat-header-info">
                <h3>{activeConv.customerName}</h3>
                <span>{activeConv.customerPhone}</span>
              </div>
              <button className="estimate-header-btn" onClick={openEstimate} title="Easy Estimate">
                <Calculator size={16} />
              </button>
              <button className="ai-suggest-header-btn" onClick={generateSuggestions} disabled={isLoadingSuggestions || chatMessages.length === 0} title="AI Smart Reply">
                {isLoadingSuggestions ? <Loader2 size={16} className="spinning" /> : <Sparkles size={16} />}
              </button>
            </div>
            <div className="messages-chat-body">
              {chatMessages.map((m) => (
                <div key={m.id} className={`msg-bubble-row ${m.sender}`}>
                  <div>
                    <div className="msg-bubble">{m.content}</div>
                    <div className="msg-time">{formatTime(m.timestamp)}</div>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {showSuggestions && (
              <div className="ai-suggestions-panel">
                <div className="ai-suggestions-header">
                  <div className="ai-suggestions-title">
                    <Sparkles size={14} />
                    <span>AI Smart Replies</span>
                  </div>
                  <button className="ai-suggestions-close" onClick={() => { setShowSuggestions(false); setSuggestions([]); }}>
                    <X size={14} />
                  </button>
                </div>
                {isLoadingSuggestions ? (
                  <div className="ai-suggestions-loading">
                    <Loader2 size={16} className="spinning" />
                    <span>Generating smart replies...</span>
                  </div>
                ) : suggestions.length > 0 ? (
                  <div className="ai-suggestions-list">
                    {suggestions.map((s, i) => (
                      <div key={i} className="ai-suggestion-item">
                        <div className="ai-suggestion-tone">{toneEmoji(s.tone)} {s.tone}</div>
                        <p className="ai-suggestion-text">{s.text}</p>
                        <div className="ai-suggestion-actions">
                          <button className="ai-suggestion-use" onClick={() => useSuggestion(s.text)}>Edit First</button>
                          <button className="ai-suggestion-send" onClick={() => sendSuggestionDirectly(s.text)}>
                            <Send size={12} /> Send
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="ai-suggestions-empty">No suggestions available</p>
                )}
              </div>
            )}

            {showEstimate && (
              <div className="estimate-panel">
                <div className="estimate-header">
                  <div className="estimate-title">
                    <Receipt size={18} />
                    <span>Easy Estimate</span>
                  </div>
                  <button className="estimate-close" onClick={() => setShowEstimate(false)}>
                    <X size={16} />
                  </button>
                </div>

                <div className="estimate-body">
                  <div className="estimate-customer-row">
                    <label>Customer</label>
                    <input
                      type="text"
                      value={estimate.customerName}
                      onChange={(e) => setEstimate((p) => ({ ...p, customerName: e.target.value }))}
                      placeholder="Customer name"
                    />
                  </div>

                  <div className="estimate-section">
                    <div className="estimate-section-label">Line Items</div>
                    <div className="estimate-items">
                      <div className="estimate-items-header">
                        <span className="ei-desc">Description</span>
                        <span className="ei-qty">Qty</span>
                        <span className="ei-price">Price</span>
                        <span className="ei-total">Total</span>
                        <span className="ei-action"></span>
                      </div>
                      {estimate.items.map((item) => (
                        <div key={item.id} className="estimate-item-row">
                          <input
                            className="ei-desc"
                            type="text"
                            placeholder="Item description"
                            value={item.description}
                            onChange={(e) => updateLineItem(item.id, "description", e.target.value)}
                          />
                          <input
                            className="ei-qty"
                            type="number"
                            min={1}
                            value={item.qty}
                            onChange={(e) => updateLineItem(item.id, "qty", Math.max(1, parseInt(e.target.value) || 1))}
                          />
                          <div className="ei-price-wrap">
                            <DollarSign size={13} />
                            <input
                              type="number"
                              min={0}
                              step="0.01"
                              value={item.price || ""}
                              onChange={(e) => updateLineItem(item.id, "price", parseFloat(e.target.value) || 0)}
                              placeholder="0.00"
                            />
                          </div>
                          <span className="ei-total">{formatCurrency(item.qty * item.price)}</span>
                          <button className="ei-remove" onClick={() => removeLineItem(item.id)} title="Remove">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                      <button className="estimate-add-item" onClick={addLineItem}>
                        <Plus size={14} /> Add Item
                      </button>
                    </div>
                  </div>

                  <div className="estimate-options">
                    <div className={`estimate-option-card ${estimate.warranty ? "active" : ""}`}>
                      <label className="estimate-option-toggle">
                        <input
                          type="checkbox"
                          checked={estimate.warranty}
                          onChange={(e) => setEstimate((p) => ({ ...p, warranty: e.target.checked }))}
                        />
                        <div className="estimate-option-icon warranty-icon">
                          <ShieldCheck size={20} />
                        </div>
                        <div className="estimate-option-info">
                          <span className="estimate-option-name">5-Year Warranty</span>
                          <span className="estimate-option-desc">Extended protection plan</span>
                        </div>
                      </label>
                      {estimate.warranty && (
                        <div className="estimate-option-price">
                          <DollarSign size={13} />
                          <input
                            type="number"
                            min={0}
                            step="0.01"
                            value={estimate.warrantyPrice || ""}
                            onChange={(e) => setEstimate((p) => ({ ...p, warrantyPrice: parseFloat(e.target.value) || 0 }))}
                          />
                        </div>
                      )}
                    </div>

                    <div className={`estimate-option-card ${estimate.delivery ? "active" : ""}`}>
                      <label className="estimate-option-toggle">
                        <input
                          type="checkbox"
                          checked={estimate.delivery}
                          onChange={(e) => setEstimate((p) => ({ ...p, delivery: e.target.checked }))}
                        />
                        <div className="estimate-option-icon delivery-icon">
                          <Truck size={20} />
                        </div>
                        <div className="estimate-option-info">
                          <span className="estimate-option-name">Delivery</span>
                          <span className="estimate-option-desc">Professional delivery service</span>
                        </div>
                      </label>
                      {estimate.delivery && (
                        <div className="estimate-option-price">
                          <DollarSign size={13} />
                          <input
                            type="number"
                            min={0}
                            step="0.01"
                            value={estimate.deliveryFee || ""}
                            onChange={(e) => setEstimate((p) => ({ ...p, deliveryFee: parseFloat(e.target.value) || 0 }))}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="estimate-tax-row">
                    <label>Tax Rate (%)</label>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step="0.01"
                      value={estimate.taxRate || ""}
                      onChange={(e) => setEstimate((p) => ({ ...p, taxRate: parseFloat(e.target.value) || 0 }))}
                      placeholder="0"
                    />
                  </div>

                  <div className="estimate-totals">
                    <div className="estimate-totals-row">
                      <span>Subtotal</span>
                      <span>{formatCurrency(estimateSubtotal)}</span>
                    </div>
                    {estimate.warranty && (
                      <div className="estimate-totals-row">
                        <span>5-Year Warranty</span>
                        <span>{formatCurrency(estimateWarrantyTotal)}</span>
                      </div>
                    )}
                    {estimate.delivery && (
                      <div className="estimate-totals-row">
                        <span>Delivery</span>
                        <span>{formatCurrency(estimateDeliveryTotal)}</span>
                      </div>
                    )}
                    {estimate.taxRate > 0 && (
                      <div className="estimate-totals-row">
                        <span>Tax ({estimate.taxRate}%)</span>
                        <span>{formatCurrency(estimateTax)}</span>
                      </div>
                    )}
                    <div className="estimate-totals-row total">
                      <span>Total</span>
                      <span>{formatCurrency(estimateTotal)}</span>
                    </div>
                  </div>

                  <button
                    className="estimate-send-btn"
                    onClick={sendEstimate}
                    disabled={estimate.items.every((i) => !i.description.trim() || i.price <= 0)}
                  >
                    <Send size={16} /> Send Estimate
                  </button>
                </div>
              </div>
            )}

            <div className="messages-chat-input">
              <button className="estimate-input-btn" onClick={openEstimate} title="Easy Estimate">
                <Calculator size={16} />
              </button>
              <button className="ai-suggest-btn" onClick={generateSuggestions} disabled={isLoadingSuggestions || chatMessages.length === 0} title="AI Smart Reply">
                {isLoadingSuggestions ? <Loader2 size={16} className="spinning" /> : <Sparkles size={16} />}
              </button>
              <input
                type="text"
                placeholder="Type a message..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                disabled={sending}
              />
              <button className="msg-send-btn" onClick={() => sendMessage()} disabled={!input.trim() || sending}>
                <Send size={18} />
              </button>
            </div>
          </>
        ) : (
          <div className="messages-empty-chat">
            <MessageSquare size={56} />
            <h3>Select a conversation</h3>
            <p>Choose a conversation from the list or start a new one</p>
          </div>
        )}
      </div>

      )}

      {showNewModal && (
        <div className="new-conv-overlay" onClick={() => { setShowNewModal(false); setNewConvMode("existing"); setCustomerSearch(""); setNewName(""); setNewPhone(""); }}>
          <div className="new-conv-modal" onClick={(e) => e.stopPropagation()}>
            <h3>New Conversation</h3>
            <div className="new-conv-tabs">
              <button
                className={`new-conv-tab ${newConvMode === "existing" ? "new-conv-tab-active" : ""}`}
                onClick={() => setNewConvMode("existing")}
              >
                <Users size={14} /> Select Customer
              </button>
              <button
                className={`new-conv-tab ${newConvMode === "manual" ? "new-conv-tab-active" : ""}`}
                onClick={() => setNewConvMode("manual")}
              >
                <UserPlus size={14} /> New Contact
              </button>
            </div>

            {newConvMode === "existing" ? (
              <div className="new-conv-customer-picker">
                <div className="new-conv-search-wrap">
                  <Search size={14} />
                  <input
                    type="text"
                    placeholder="Search customers..."
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="new-conv-customer-list">
                  {filteredCustomers.length === 0 ? (
                    <div className="new-conv-no-customers">No customers found</div>
                  ) : (
                    filteredCustomers.map((c) => (
                      <button key={c.id} className="new-conv-customer-row" onClick={() => selectExistingCustomer(c.id)}>
                        <div className="new-conv-customer-avatar">{c.name.charAt(0)}</div>
                        <div className="new-conv-customer-info">
                          <span className="new-conv-customer-name">{c.name}</span>
                          <span className="new-conv-customer-phone">{c.phone}</span>
                        </div>
                        <span className={`new-conv-customer-status new-conv-status-${c.status}`}>
                          {c.status.replace("_", " ")}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            ) : (
              <>
                <label>Customer Name</label>
                <input
                  type="text"
                  placeholder="Enter customer name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  autoFocus
                />
                <label>Phone Number</label>
                <input
                  type="text"
                  placeholder="(555) 123-4567"
                  value={newPhone}
                  onChange={(e) => setNewPhone(e.target.value)}
                />
                <div className="new-conv-actions">
                  <button className="new-conv-cancel" onClick={() => { setShowNewModal(false); setNewConvMode("existing"); setNewName(""); setNewPhone(""); }}>Cancel</button>
                  <button
                    className="new-conv-submit"
                    onClick={createConversation}
                    disabled={!newName.trim() || !newPhone.trim() || creatingConv}
                  >
                    {creatingConv ? "Creating..." : "Start Conversation"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
